<template>
  <div class="plan-markets-panel" data-testid="plan-markets-panel">
    <!-- Market list -->
    <div
      v-if="editingMarket == null"
      class="plan-prices-list"
      data-testid="plan-prices-list"
    >
      <div
        v-for="market in currentMarkets"
        :key="`${market.currency}-${market.provider_code}`"
        class="plan-prices-row"
        :data-testid="`plan-prices-market-row-${market.currency}-${market.provider_code}`"
      >
        <span
          class="market-currency"
          :data-testid="`plan-prices-currency-${market.currency}-${market.provider_code}`"
        >{{ market.currency }}</span>
        <span
          class="market-gateway"
          :data-testid="`plan-prices-gateway-${market.currency}-${market.provider_code}`"
        >{{ market.provider_code }}</span>
        <span
          class="market-price"
          :data-testid="`plan-prices-price-${market.currency}-${market.provider_code}`"
        >{{ formatPrice(market) }}</span>
        <!--
          The countries a market row serves are EDITABLE in the form below but
          were never shown in this list, so the only way to check which markets
          a price covered was to open each row. That made a country-scoped
          change — a BRL/asaas row offered in CL and EC — unverifiable from the
          screen that exists to show it.
        -->
        <span
          class="market-countries"
          :data-testid="`plan-prices-countries-${market.currency}-${market.provider_code}`"
        >{{ (market.countries ?? []).join(', ') || '—' }}</span>
        <span
          class="market-stripe"
          :data-testid="`plan-prices-stripe-${market.currency}-${market.provider_code}`"
        >{{ market.provider_price_ref ?? market.stripe_price_id ?? '—' }}</span>
        <Button
          :label="t('Editar')"
          severity="secondary"
          size="small"
          :data-testid="`plan-prices-edit-market-${market.currency}-${market.provider_code}`"
          @click="startEditMarket(market)"
        />
      </div>

      <div class="plan-prices-add" data-testid="plan-prices-add-section">
        <Button
          v-if="canAddMarket"
          :label="t('+ Adicionar mercado')"
          severity="secondary"
          data-testid="plan-prices-add-market-btn"
          @click="startAddMarket"
        />
      </div>
    </div>

    <!-- Inline add/edit form -->
    <div
      v-else
      class="plan-prices-edit-form"
      data-testid="plan-prices-edit-form"
    >
      <div class="form-fields">
        <!-- Currency: fixed in edit mode, selectable in add mode -->
        <div v-if="isAddMode" class="field-row">
          <select-list
            v-model="editingMarket.currency"
            elem-id="plan-prices-edit-currency"
            :label="t('Moeda')"
            :itens-select="availableCurrencyOptions"
            data-testid="plan-prices-edit-currency"
          />
        </div>
        <div v-else class="field-row">
          <label class="field-label">{{ t('Moeda') }}</label>
          <span
            class="field-value-static"
            data-testid="plan-prices-edit-currency-static"
          >{{ editingMarket.currency }}</span>
        </div>

        <!-- Gateway: always selectable; options filtered to gateways that support the chosen currency -->
        <div class="field-row">
          <select-list
            v-model="editingMarket.provider_code"
            elem-id="plan-prices-edit-gateway"
            :label="t('Gateway')"
            :itens-select="availableGatewayOptions"
            data-testid="plan-prices-edit-gateway"
          />
        </div>

        <!-- Countries: billing-ready countries from the market-countries endpoint (D6) -->
        <div class="field-row">
          <select-list-multiple
            v-model="editingMarket.countries"
            elem-id="plan-prices-edit-countries"
            :label="t('Países')"
            :itens-select="billingReadyCountries"
            data-testid="plan-prices-edit-countries"
            @update:model-value="countriesError = null"
          />
          <small
            v-if="countriesError"
            class="field-error"
            data-testid="plan-prices-countries-error"
          >{{ countriesError }}</small>
        </div>

        <div class="field-row">
          <input-currency
            v-model="editPriceMajor"
            elem-id="plan-prices-edit-price"
            :label="t('Preço')"
            :currency="editingMarket.currency"
            :currency-prefix="editingLocale"
            data-testid="plan-prices-edit-price"
          />
        </div>

        <!--
          The Stripe Price ID field lived here until 2026-08-21. It required an
          operator to open the Stripe dashboard, create a Price by hand and paste
          `price_…` back in — two systems holding one fact, with nothing keeping
          them honest. The backend now creates the price at the gateway when this
          form is saved and stores the reference itself, so there is nothing left
          to type. The minted reference is shown read-only in the table above.
        -->
      </div>

      <div class="edit-form-actions">
        <Button
          :label="t('Salvar mercado')"
          :disabled="isSaving"
          data-testid="plan-markets-panel-save-market-btn"
          @click="handleSaveMarket"
        />
        <Button
          :label="t('Cancelar')"
          severity="secondary"
          size="small"
          data-testid="plan-prices-cancel-edit"
          @click="cancelEdit"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { eventBus } from '../../../eventBus'
import { log } from '../../../utils/DebugLogger'
import { useSubscriptionAdmin } from '../../../composables/useSubscriptionAdmin'
import { usePaymentGatewayRegistry } from '../../../composables/usePaymentGatewayRegistry'
import { useMarketCountries } from '../../../composables/useMarketCountries'
import { currencyFactor } from '../../../utils/currencyMinorUnits'
import type { PlanMarket } from '../../../composables/useSubscriptionAdmin'

const { t, n } = useI18n()
const { saveMarket, listPlans, groupedPlans } = useSubscriptionAdmin()
const { currenciesForProvider, featureMap, loadMap } = usePaymentGatewayRegistry()
// D6: countries + currencies come from the market-countries endpoint
// (locations-service annotated by MarketPolicy) — never a hardcoded list.
const { billingReadyCountries, marketCurrencies, load: loadMarketCountries } = useMarketCountries()

const props = defineProps<{
  planId: number
}>()

const emit = defineEmits<{
  (e: 'changed'): void
}>()

// Display-formatting locale per currency — NOT a market list.
const CURRENCY_LOCALE: Record<string, string> = {
  BRL: 'pt-BR',
  USD: 'en-US',
  CLP: 'es-CL',
}

onMounted(() => {
  // featureMap is module-scope: without this the Gateway options are empty for
  // any session that lands here without having visited the gateways page first.
  loadMap().catch((err) => {
    log('PlanMarketsPanel', 'onMounted', 'Erro ao carregar mapa de gateways', err)
  })
  loadMarketCountries().catch((err) => {
    log('PlanMarketsPanel', 'onMounted', 'Erro ao carregar países de mercado', err)
  })
})

const isSaving = ref(false)
const countriesError = ref<string | null>(null)

/** Editing state: set when user clicks Editar on a market row or + Adicionar mercado. */
const editingMarket = ref<{
  currency: string
  price_cents: number
  provider_code: string
  countries: string[]
} | null>(null)
const isAddMode = ref(false)

/** Derived from groupedPlans so it stays reactive after listPlans() refreshes. */
const currentPlan = computed(() =>
  groupedPlans.value.find((g) => g.id === props.planId) ?? null,
)

const currentMarkets = computed(() => currentPlan.value?.markets ?? [])

/**
 * canAddMarket — true when there is at least one valid (currency, gateway) combination
 * that does not yet have a live row. Since multiple gateways can share a currency,
 * we cannot block on "all currencies taken" alone.
 *
 * D6: currencies come from marketCurrencies (billing-ready countries), not a
 * hardcoded list. While either feed is still loading we stay permissive (true).
 * When every combination already has a row the answer is false (the previous
 * tautological `return true` fallthrough was a latent bug).
 */
const canAddMarket = computed<boolean>(() => {
  if (!featureMap.value) return true
  if (marketCurrencies.value.length === 0) return true
  // Collect all known gateways from the featureMap
  const allGateways = new Set<string>()
  for (const providers of Object.values(featureMap.value)) {
    for (const code of Object.keys(providers)) allGateways.add(code)
  }
  // For each (currency, gateway) pair, check if a market row already exists
  for (const currency of marketCurrencies.value) {
    for (const gateway of allGateways) {
      const supported = currenciesForProvider(gateway)
      if (supported.includes('*') || supported.includes(currency)) {
        const exists = currentMarkets.value.some(
          (m) => m.currency === currency && m.provider_code === gateway,
        )
        if (!exists) return true
      }
    }
  }
  return false
})

const availableCurrencyOptions = computed(() =>
  marketCurrencies.value.map((c) => ({ ID: c, Name: c })),
)

/**
 * availableGatewayOptions — gateways whose currency list includes the currently
 * selected currency (or '*' for any currency). Derived from usePaymentGatewayRegistry.
 */
const availableGatewayOptions = computed(() => {
  const currency = editingMarket.value?.currency ?? ''
  if (!featureMap.value || !currency) return []
  const seen = new Set<string>()
  const options: { ID: string; Name: string }[] = []
  for (const providers of Object.values(featureMap.value)) {
    for (const [code, entry] of Object.entries(providers)) {
      if (seen.has(code)) continue
      const supported: string[] = entry.currencies ?? []
      if (supported.includes('*') || supported.includes(currency)) {
        seen.add(code)
        options.push({ ID: code, Name: code })
      }
    }
  }
  return options
})

function formatPrice(market: PlanMarket): string {
  const locale = CURRENCY_LOCALE[market.currency] ?? 'pt-BR'
  const factor = currencyFactor(locale, market.currency)
  const majorValue = market.price_cents / factor
  return n(majorValue, { style: 'currency', currency: market.currency, currencyDisplay: 'symbol' })
}

// Locale for the market being edited — drives InputCurrency's symbol + decimals
const editingLocale = computed(() =>
  editingMarket.value ? (CURRENCY_LOCALE[editingMarket.value.currency] ?? 'pt-BR') : 'pt-BR',
)

// Convert between major (display) and minor (wire) units for the editing market.
const editPriceMajor = computed({
  get: () => {
    if (!editingMarket.value) return 0
    const currency = editingMarket.value.currency
    const locale = CURRENCY_LOCALE[currency] ?? 'pt-BR'
    const factor = currencyFactor(locale, currency)
    return editingMarket.value.price_cents / factor
  },
  set: (val: number) => {
    if (!editingMarket.value) return
    const currency = editingMarket.value.currency
    const locale = CURRENCY_LOCALE[currency] ?? 'pt-BR'
    const factor = currencyFactor(locale, currency)
    editingMarket.value.price_cents = Math.round(val * factor)
  },
})

function startEditMarket(market: PlanMarket): void {
  isAddMode.value = false
  editingMarket.value = {
    currency: market.currency,
    price_cents: market.price_cents,
    provider_code: market.provider_code ?? '',
    countries: [...(market.countries ?? [])],
  }
}

function startAddMarket(): void {
  isAddMode.value = true
  // Default to the first market currency (BRL first by sort) once loaded.
  const firstCurrency = marketCurrencies.value[0] ?? ''
  editingMarket.value = {
    currency: firstCurrency,
    price_cents: 0,
    provider_code: '',
    countries: [],
  }
}

function cancelEdit(): void {
  editingMarket.value = null
  isAddMode.value = false
  countriesError.value = null
}

async function handleSaveMarket(): Promise<void> {
  if (!currentPlan.value || !editingMarket.value) return
  if (editingMarket.value.countries.length === 0) {
    countriesError.value = t('Selecione ao menos um país')
    return
  }
  try {
    isSaving.value = true
    const market: PlanMarket = {
      currency: editingMarket.value.currency,
      price_cents: editingMarket.value.price_cents,
      provider_code: editingMarket.value.provider_code,
      countries: editingMarket.value.countries,
      // provider_price_ref is deliberately NOT sent: it is server-minted, and
      // the contract refuses a client-supplied one for every gateway.
    }
    await saveMarket(currentPlan.value, market)
    log('PlanMarketsPanel', 'handleSaveMarket', 'Mercado salvo', {
      planId: props.planId,
      currency: market.currency,
      provider_code: market.provider_code,
    })
    eventBus.emit('globalMessage.success', t('Mercado salvo com sucesso'))
    editingMarket.value = null
    isAddMode.value = false
    // Refresh plans list — currentPlan computed re-derives from groupedPlans reactively
    await listPlans()
    eventBus.emit('staffSubscription.refresh')
    emit('changed')
  } catch (err) {
    log('PlanMarketsPanel', 'handleSaveMarket', 'Erro ao salvar mercado', err)
    eventBus.emit('globalMessage.error', t('Erro ao salvar mercado'))
  } finally {
    isSaving.value = false
  }
}

defineExpose({ editingMarket, isAddMode, availableCurrencies: availableCurrencyOptions, canAddMarket, countriesError })
</script>

<style scoped>
.plan-markets-panel {
  margin-top: 16px;
}

.plan-prices-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.plan-prices-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid var(--surface-border, #e0e0e0);
}

.market-currency {
  font-weight: 600;
  min-width: 48px;
}

.market-countries {
  min-width: 90px;
  font-size: 0.85rem;
  opacity: 0.85;
}

.market-gateway {
  color: var(--text-color-secondary, #6c757d);
  font-size: 0.85rem;
  min-width: 64px;
}

.market-price {
  flex: 1;
}

.market-stripe {
  color: var(--text-color-secondary, #6c757d);
  font-size: 0.85rem;
  flex: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.plan-prices-add {
  padding-top: 8px;
}

.plan-prices-edit-form .form-fields {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.field-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.field-label {
  font-size: 0.875rem;
  font-weight: 500;
}

.field-value-static {
  font-weight: 600;
  padding: 6px 0;
}

.field-full {
  flex: 1 1 100%;
}

.edit-form-actions {
  display: flex;
  gap: 8px;
  margin-top: 16px;
}

.field-error {
  color: var(--red-500, #ef4444);
  font-size: 0.8rem;
}
</style>
